/* eslint-disable */
import React, { useEffect, useMemo } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';

export type PersonPackageSelection = {
  ProductId: string;
  PackageId: string;
  SlotKey: string;
};

export type PackageOptionUi = {
  ProductId: string;
  ProductName: string;
  PackageId: string;
  PackageName: string;
  Quantity: number;
};

type Props = {
  options: PackageOptionUi[];
  value?: PersonPackageSelection[];
  onChange?: (value: PersonPackageSelection[]) => void;
  usedQtyByPackageKey?: Record<string, number>;
  disabled?: boolean;
};

type SlotMeta = {
  SlotKey: string;
  PackageKey: string;
  ProductId: string;
  PackageId: string;
  Label: string;
};

const buildPackageKey = (productId: string, packageId: string) => `${productId}__${packageId}`;

const buildSlotKey = (productId: string, packageId: string, index: number) => `${productId}__${packageId}__${index}`;

export const PackageSingleMultiSelectPicker: React.FC<Props> = ({
  options,
  value = [],
  onChange,
  usedQtyByPackageKey = {},
  disabled = false,
}) => {
  const { t } = useTranslation();
  const slotMetaMap = useMemo(() => {
    const map: Record<string, SlotMeta> = {};

    (options || []).forEach((pkg) => {
      const total = Number(pkg.Quantity || 0);
      const productId = String(pkg.ProductId);
      const packageId = String(pkg.PackageId);
      const packageKey = buildPackageKey(productId, packageId);

      for (let i = 1; i <= total; i++) {
        const slotKey = buildSlotKey(productId, packageId, i);

        map[slotKey] = {
          SlotKey: slotKey,
          PackageKey: packageKey,
          ProductId: productId,
          PackageId: packageId,
          Label: `${pkg.PackageName}  - lượt ${i})`,
        };
      }
    });

    return map;
  }, [options]);

  const selectedValues = useMemo(() => {
    return (value || []).map((x) => String(x?.SlotKey || '')).filter(Boolean);
  }, [value]);

  const selectOptions = useMemo(() => {
    const result: { value: string; label: string }[] = [];

    (options || []).forEach((pkg) => {
      const total = Number(pkg.Quantity || 0);
      const productId = String(pkg.ProductId);
      const packageId = String(pkg.PackageId);
      const packageKey = buildPackageKey(productId, packageId);
      const usedByPrevious = Number(usedQtyByPackageKey[packageKey] || 0);

      for (let i = usedByPrevious + 1; i <= total; i++) {
        const slotKey = buildSlotKey(productId, packageId, i);

        result.push({
          value: slotKey,
          label: `${pkg.PackageName} - lượt ${i}`,
        });
      }
    });

    // luôn giữ option đang được chọn để Select không bị mất tag
    selectedValues.forEach((slotKey) => {
      const existed = result.some((x) => x.value === slotKey);
      const meta = slotMetaMap[slotKey];
      if (!existed && meta) {
        result.push({
          value: meta.SlotKey,
          label: meta.Label,
        });
      }
    });

    return result;
  }, [options, usedQtyByPackageKey, selectedValues, slotMetaMap]);

  useEffect(() => {
    const valid = (value || []).filter((x) => !!slotMetaMap[String(x?.SlotKey || '')]);

    if (valid.length !== (value || []).length) {
      onChange?.(valid);
    }
  }, [slotMetaMap, value, onChange]);

  const handleChange = (slotKeys: string[]) => {
    const next = (slotKeys || [])
      .map((slotKey) => slotMetaMap[String(slotKey)])
      .filter(Boolean)
      .map((meta) => ({
        ProductId: meta.ProductId,
        PackageId: meta.PackageId,
        SlotKey: meta.SlotKey,
      }));

    onChange?.(next);
  };

  return (
    <Select
      mode="multiple"
      allowClear
      disabled={disabled}
      value={selectedValues}
      style={{ width: '100%' }}
      placeholder={t('Chọn gói sử dụng')}
      maxTagCount="responsive"
      optionFilterProp="label"
      onChange={(vals) => handleChange(vals as string[])}
      options={selectOptions}
    />
  );
};

export default PackageSingleMultiSelectPicker;
